import React from 'react';
import EmbedConsentGate from 'volto-ministerio-publico-federal/components/CookieConsent/EmbedConsentGate';
import type { YoutubeVideoRef } from './helpers';

interface YoutubeDialogProps {
  /** Vídeo em cartaz; `null` mantém o diálogo fechado. */
  video: YoutubeVideoRef | null;
  onClose: () => void;
}

/**
 * Player do YouTube em modal, no `<dialog>` nativo.
 *
 * O elemento é renderizado sempre (fechado, ele já nasce oculto pelo próprio
 * navegador) e aberto/fechado por `showModal()`/`close()` — assim o foco entra
 * e volta pelo mecanismo nativo, sem armadilha de foco escrita à mão.
 *
 * O `<iframe>` só existe enquanto há vídeo em cartaz: além de não pesar a capa
 * com um player por card, é o que garante que o áudio pare ao fechar. O
 * `onClose` é obrigatório justamente por isso — Esc fecha o `<dialog>` por
 * conta própria, sem passar pelo botão de fechar, e sem esse handler o estado
 * do React continuaria achando que o modal está aberto.
 *
 * `youtube-nocookie.com` é o domínio do YouTube que não grava cookies de
 * rastreio antes de o visitante dar play.
 */
export const YoutubeDialog: React.FC<YoutubeDialogProps> = ({
  video,
  onClose,
}) => {
  const ref = React.useRef<HTMLDialogElement>(null);

  React.useEffect(() => {
    const dialog = ref.current;
    // `showModal` não existe no jsdom antigo nem em navegadores sem suporte:
    // sem ele o modal não abre, mas o card continua com o link para o vídeo.
    if (!dialog || typeof dialog.showModal !== 'function') return;
    if (video && !dialog.open) dialog.showModal();
    if (!video && dialog.open) dialog.close();
  }, [video]);

  // Clique no fundo. Ouvinte imperativo, e não `onClick` no JSX: o alvo é o
  // próprio `<dialog>` (é assim que o backdrop se comporta), o que a análise
  // estática lê como clique num elemento não interativo.
  React.useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return undefined;
    const handle = (event: MouseEvent) => {
      if (event.target === dialog) onClose();
    };
    dialog.addEventListener('click', handle);
    return () => dialog.removeEventListener('click', handle);
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      aria-label={video?.title ? `Vídeo: ${video.title}` : 'Vídeo'}
      className="mpf-video-dialog"
      onClose={onClose}
    >
      {video && (
        <div className="relative w-full">
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar vídeo"
            className="absolute -top-11 right-0 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
              className="h-5 w-5"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>

          <div className="aspect-video w-full overflow-hidden rounded-md bg-black">
            <EmbedConsentGate
              provider="YouTube"
              href={`https://www.youtube.com/watch?v=${video.id}`}
            >
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0`}
                title={video.title || 'Vídeo do YouTube'}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="h-full w-full border-0"
              />
            </EmbedConsentGate>
          </div>
        </div>
      )}
    </dialog>
  );
};

export default YoutubeDialog;
